import React, { useState, useEffect } from 'react';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemDecorator,
  ListItemContent,
  ListItemButton,
  Divider,
} from '@mui/joy';
import {
  Rocket as RocketIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Lightbulb as TipIcon,
  Gauge as SpeedIcon,
  Eye as ViewIcon,
  PencilSimple as EditIcon,
  Sparkle as AIIcon,
  Question as HelpIcon,
  PlayCircle as VideoIcon,
  Gear as SettingsIcon,
  CloudArrowUp as DeployIcon,
  ClockCounterClockwise as HistoryIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { QuickActions } from './QuickActions';
import { ContentSuggestions } from '../../components/cms/ContentSuggestions';
import { PageTemplates } from '../../components/cms/PageTemplates';

interface DashboardData {
  site?: any;
  pages: any[];
  stats: {
    totalPages: number;
    publishedPages: number;
    draftPages: number;
    lastUpdated?: string;
    seoScore?: number;
    performanceScore?: number;
    hasSpecialOffer: boolean;
    photoCount: number;
  };
  quickTips: string[];
}

export const CMSDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  // Load sites
  const { data: sites, isLoading: loadingSites } = useQuery({
    queryKey: ['cms-sites'],
    queryFn: async () => {
      const response = await api.get<any>('/api/cms/sites/');
      return (response.data?.results || response.data || []);
    },
  });

  const site = sites?.[0];

  // Load pages if site exists
  const { data: pages = [], isLoading: loadingPages } = useQuery({
    queryKey: ['cms-pages', site?.id],
    queryFn: async () => {
      if (!site?.id) return [];
      const response = await api.get<any>(`/api/cms/sites/${site.id}/pages/`);
      return (response.data?.results || response.data || []);
    },
    enabled: !!site?.id,
  });

  // Calculate stats
  const stats = React.useMemo(() => {
    if (!pages) return {
      totalPages: 0,
      publishedPages: 0,
      draftPages: 0,
      seoScore: 0,
      performanceScore: 0,
      hasSpecialOffer: false,
      photoCount: 0,
    };

    return {
      totalPages: pages.length,
      publishedPages: pages.filter((p: any) => p.status === 'published').length,
      draftPages: pages.filter((p: any) => p.status === 'draft').length,
      lastUpdated: pages[0]?.updated_at,
      seoScore: pages.length >= 5 ? 85 : pages.length * 17,
      performanceScore: 92, // Mock for now
      hasSpecialOffer: pages.some((p: any) => p.slug.includes('offer') || p.slug.includes('special')),
      photoCount: 12, // Mock for now
    };
  }, [pages]);

  const quickTips = [
    "💡 Add customer testimonials to build trust - it increases conversions by 270%!",
    "📸 Upload photos from recent jobs to showcase your work",
    "🎯 Create location-specific pages to rank higher in local searches",
    "⏰ Add a limited-time offer to create urgency and boost sales",
    "📱 Your website automatically looks great on mobile devices",
    "🔍 We handle all the technical SEO so you can focus on your business",
    "⚡ Pages load super fast thanks to our optimization",
    "📊 Check your site performance score to see how you're doing",
  ];

  // Rotate tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % quickTips.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [quickTips.length]);

  if (loadingSites) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No site yet - show big CTA
  if (!site) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Card variant="soft" sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <RocketIcon size={64} color="var(--joy-palette-primary-500)" style={{ marginBottom: 24 }} />
            <Typography level="h1" sx={{ mb: 2 }}>
              Welcome to Your Website Builder!
            </Typography>
            <Typography level="body-lg" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Create a professional, SEO-optimized website for your roofing business in just 5 minutes. 
              No technical skills needed!
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                size="lg"
                variant="solid"
                startDecorator={<RocketIcon />}
                onClick={() => navigate('/cms/sites/quick-setup')}
              >
                Quick Setup (Recommended)
              </Button>
              <Button
                size="lg"
                variant="outlined"
                color="primary"
                onClick={() => navigate('/cms/sites/new')}
              >
                Custom Setup
              </Button>
            </Box>
            <Button
              variant="plain"
              startDecorator={<VideoIcon />}
              sx={{ mt: 3 }}
              onClick={() => setShowVideo(true)}
            >
              Watch 2-minute tutorial
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="h1">Website Dashboard</Typography>
              <PageHelpButton />
            </Box>
            <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
              {site.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="solid"
              color="success"
              startDecorator={<DeployIcon />}
              onClick={() => navigate(`/cms/sites/${site.id}/deploy`)}
            >
              Deploy Site
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<EditIcon />}
              onClick={() => navigate(`/cms/sites/${site.id}/pages`)}
            >
              Edit Pages
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<ViewIcon />}
              onClick={() => window.open(`https://${site.domain}`, '_blank')}
            >
              View Live Site
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<HistoryIcon />}
              onClick={() => navigate(`/cms/sites/${site.id}/deploy/history`)}
            >
              Deploy History
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<SettingsIcon />}
              onClick={() => navigate(`/cms/sites/${site.id}/settings`)}
            >
              Settings
            </Button>
          </Box>
        </Box>

        {/* Quick Tip */}
        <Alert
          variant="soft"
          color="primary"
          startDecorator={<TipIcon />}
          sx={{ 
            transition: 'all 0.3s',
            cursor: 'pointer',
          }}
          onClick={() => setCurrentTip((prev) => (prev + 1) % quickTips.length)}
        >
          <Typography level="body-sm">
            {quickTips[currentTip]}
          </Typography>
        </Alert>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Total Pages</Typography>
              <Typography level="h2">{stats.totalPages}</Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {stats.publishedPages} published, {stats.draftPages} drafts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>SEO Score</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress
                  determinate
                  value={stats.seoScore}
                  size="lg"
                  sx={{
                    '--CircularProgress-size': '60px',
                    '--CircularProgress-progressColor': 
                      stats.seoScore >= 80 ? 'success.500' : 
                      stats.seoScore >= 60 ? 'warning.500' : 'danger.500'
                  }}
                >
                  <Typography level="body-md">{stats.seoScore}%</Typography>
                </CircularProgress>
                <Box>
                  <Typography level="body-xs">
                    {stats.seoScore >= 80 ? 'Excellent!' : 
                     stats.seoScore >= 60 ? 'Good' : 'Needs Work'}
                  </Typography>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => navigate(`/cms/sites/${site.id}/seo`)}
                  >
                    Improve
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Performance</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SpeedIcon size={40} color="var(--joy-palette-success-500)" />
                <Box>
                  <Typography level="h3">{stats.performanceScore}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Lighthouse Score
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Quick Stats</Typography>
              <List size="sm">
                <ListItem>
                  <ListItemDecorator>
                    {stats.hasSpecialOffer ? <CheckIcon color="var(--joy-palette-success-500)" /> : <WarningIcon color="var(--joy-palette-warning-500)" />}
                  </ListItemDecorator>
                  <Typography level="body-xs">
                    {stats.hasSpecialOffer ? 'Special offer active' : 'No special offer'}
                  </Typography>
                </ListItem>
                <ListItem>
                  <ListItemDecorator>
                    {stats.photoCount > 10 ? <CheckIcon color="var(--joy-palette-success-500)" /> : <WarningIcon color="var(--joy-palette-warning-500)" />}
                  </ListItemDecorator>
                  <Typography level="body-xs">
                    {stats.photoCount} photos uploaded
                  </Typography>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Quick Actions & Templates */}
        <Grid xs={12} lg={8}>
          <Box sx={{ mb: 3 }}>
            <QuickActions siteId={site.id} onActionComplete={() => window.location.reload()} />
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box>
            <PageTemplates 
              siteId={site.id} 
              existingPages={pages.map((p: any) => p.slug)}
              onPageCreated={() => window.location.reload()}
            />
          </Box>
        </Grid>

        {/* Right Column - Suggestions & Help */}
        <Grid xs={12} lg={4}>
          <Box sx={{ mb: 3 }}>
            <ContentSuggestions
              siteId={site.id}
              pages={pages}
              onAddContent={(suggestion) => {
                // Navigate based on suggestion type
                if (suggestion.id === 'homepage') {
                  navigate(`/cms/sites/${site.id}/pages/new?template=home`);
                } else if (suggestion.id === 'contact') {
                  navigate(`/cms/sites/${site.id}/pages/new?template=contact`);
                } else {
                  navigate(`/cms/sites/${site.id}/pages/new`);
                }
              }}
            />
          </Box>

          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Need Help?
              </Typography>
              
              <List>
                <ListItemButton onClick={() => setShowVideo(true)}>
                  <ListItemDecorator>
                    <VideoIcon />
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-md">Watch Tutorial Videos</Typography>
                    <Typography level="body-sm">Learn how to use every feature</Typography>
                  </ListItemContent>
                </ListItemButton>
                
                <ListItemButton onClick={() => window.open('/help/cms', '_blank')}>
                  <ListItemDecorator>
                    <HelpIcon />
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-md">Read Help Guide</Typography>
                    <Typography level="body-sm">Step-by-step instructions</Typography>
                  </ListItemContent>
                </ListItemButton>
                
                <ListItemButton onClick={() => navigate('/cms/support')}>
                  <ListItemDecorator>
                    <AIIcon />
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-md">Get AI Assistance</Typography>
                    <Typography level="body-sm">Chat with our AI helper</Typography>
                  </ListItemContent>
                </ListItemButton>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Video Modal */}
      {showVideo && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
          }}
          onClick={() => setShowVideo(false)}
        >
          <Box
            sx={{
              width: '80%',
              maxWidth: 800,
              aspectRatio: '16/9',
              bgcolor: 'black',
              borderRadius: 'md',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/dQw4w9WgXcQ"
              title="CMS Tutorial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
